import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AdminCreateLessonExcusalData, adminLessonExcusalsApi } from '@/services';

export const adminLessonExcusalsKeys = {
  all: ['admin', 'lesson-excusals'] as const,
  list: () => ['admin', 'lesson-excusals', 'list'] as const,
  detail: (id?: string) => ['admin', 'lesson-excusals', 'detail', id ?? null] as const,
  options: (studentId: string, date: string) => ['admin', 'lesson-excusals', 'options', studentId, date] as const,
};

export function useAdminLessonExcusalsListQuery() {
  return useQuery({
    queryKey: adminLessonExcusalsKeys.list(),
    queryFn: () => adminLessonExcusalsApi.list(),
  });
}

export function useAdminLessonExcusalQuery(id?: string) {
  return useQuery({
    queryKey: adminLessonExcusalsKeys.detail(id),
    queryFn: () => adminLessonExcusalsApi.detail(id as string),
    enabled: Boolean(id),
  });
}

export function useDeleteLessonExcusalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminLessonExcusalsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminLessonExcusalsKeys.all }),
  });
}

/** The lessons a student can be excused from on one day. Idle until both are answered. */
export function useAdminLessonExcusalOptionsQuery(studentId: string, date: string) {
  return useQuery({
    queryKey: adminLessonExcusalsKeys.options(studentId, date),
    queryFn: () => adminLessonExcusalsApi.options(studentId, date),
    enabled: Boolean(studentId && date),
  });
}

export function useRecordLessonExcusalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminCreateLessonExcusalData) => adminLessonExcusalsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminLessonExcusalsKeys.all }),
  });
}
